/**
 * Controller for the CD additional-information form (scenarios 5 & 7).
 *
 * Responsibilities:
 *  - Find inputs inside [data-component="cd-outcome-additional-information"]
 *    matching the field schema (Webflow data-name attributes).
 *  - Bind blur (text) / change (select, radio, checkbox) events.
 *  - On each event: read value, validate, update state, toggle error styling,
 *    fire partial save via the existing simple-lead-processor edge function.
 *  - Bind the form's submit button; on submit validate all → POST to the new
 *    dcapi endpoint → on accept perform redirect.
 *
 * All identifiers are generic ("additional information") per the PRD.
 */

import { getValidationRegistry } from '../formatting/validation/format-validators';
import { ADDITIONAL_INFO_FIELDS, findFieldRuleByWebflowName } from './field-schema';
import type { AdditionalInfoFieldRule } from './field-schema';
import type { AdditionalInfoFormConfig, AdditionalInfoState } from './types';
import { sendPartialSave } from './partial-save';
import { submitAdditionalInfo, performAcceptRedirect } from './submit-handler';
import { applyDateMask } from './date-mask';
import { applyPhoneMask } from './phone-mask';

const DATE_RULE_TYPES = new Set(['future_date', 'date']);

function ruleNeedsDateMask(rule: AdditionalInfoFieldRule): boolean {
  return rule.rules.some((r) => DATE_RULE_TYPES.has(r.type));
}

function ruleNeedsPhoneMask(rule: AdditionalInfoFieldRule): boolean {
  return rule.rules.some((r) => r.type === 'phone');
}

const HOST_SELECTOR = '[data-component="cd-outcome-additional-information"]';
const FIELD_ERROR_CLASS = 'field-error';

export class AdditionalInfoFormController {
  private state: AdditionalInfoState = { values: {}, errors: {}, touched: {} };
  private host: HTMLElement | null = null;
  private inputs = new Map<string, HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>();
  private fieldRulesByPayloadKey = new Map<string, AdditionalInfoFieldRule>();
  private boundButtons = new WeakSet<HTMLElement>();
  private submitting = false;

  constructor(private config: AdditionalInfoFormConfig) {
    ADDITIONAL_INFO_FIELDS.forEach((f) => this.fieldRulesByPayloadKey.set(f.payloadKey, f));
  }

  /**
   * Initialize. Safe to call before the host element is visible — fields are
   * bound when the host first enters the DOM (already on Webflow page) or
   * when a MutationObserver detects them later.
   */
  /** Always-on debug logger for the O5/O7 additional-info show path. */
  private dlog(...args: unknown[]): void {
    // eslint-disable-next-line no-console
    console.log('[ADDL-INFO]', ...args);
  }

  public init(): void {
    this.dlog('init() called. Looking for host:', HOST_SELECTOR);
    this.host = document.querySelector(HOST_SELECTOR);

    if (this.host) {
      this.dlog('Host found immediately in DOM.');
      this.bindHost(this.host);
      return;
    }

    // Host not in DOM yet — watch for it. Webflow pre-places components but
    // some templates inject them lazily.
    this.dlog('Host NOT in DOM yet — installing MutationObserver to wait for it.');
    const observer = new MutationObserver(() => {
      const found = document.querySelector(HOST_SELECTOR);
      if (found) {
        this.dlog('Host appeared in DOM via MutationObserver.');
        this.host = found as HTMLElement;
        observer.disconnect();
        this.bindHost(this.host);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  private bindHost(host: HTMLElement): void {
    this.dlog('bindHost() start. Host tag/class:', host.tagName, host.className);
    // Put the host back where the other outcomes live. The Webflow restructure
    // (div→section, "un-nested") left this section as a direct child of <body>,
    // positioned AFTER the footer. showComponent() correctly sets display:block
    // on it, but it then paints below the footer — off-screen — so the content
    // area looks empty and the O5/O7 "doesn't show". Relocate it into the same
    // container as the other CD outcomes so it renders in the content flow.
    this.relocateHostIntoOutcomeContainer(host);

    // Opt the entire additional-info host out of the global format-engine so phone /
    // currency / SSN auto-formatting can't mangle raw digit fields (bank account,
    // routing number, driver's license). This module owns validation for fields here.
    host.setAttribute('data-no-format', 'true');

    // Reveal every multi-step step block once at bind. Re-asserted on every
    // host show via watchVisibility() — see revealSteps() for the full why.
    this.revealSteps(host);

    // Stop Webflow's multi-step JS from masking/clipping this form.
    this.neutralizeWebflowMultistep(host);
    // ...and re-enable the Submit button Webflow's multistep left disabled.
    this.enableSubmitButton(host);
    this.dlog('neutralize + enableSubmit done. multistep attrs:',
      host.querySelectorAll('[data-form="multistep"]').length,
      'submit disabled?:',
      (host.querySelector('[data-form="submit-btn"]') as HTMLInputElement | null)?.disabled);

    for (const rule of ADDITIONAL_INFO_FIELDS) {
      const input = this.findInput(host, rule.webflowName);
      if (!input) {
        if (this.config.debug) {
          console.warn(`[additional-info] field not found in Webflow: "${rule.webflowName}"`);
        }
        continue;
      }
      this.inputs.set(rule.payloadKey, input);
      this.bindInput(input, rule);
    }

    this.bindSubmit(host);
    this.watchVisibility(host);
  }

  /**
   * Move the additional-info <section> into the same parent container as the
   * other CD outcome components, if it isn't already there.
   *
   * Why: the Webflow restructure left this section as a direct child of <body>,
   * AFTER the footer. Every other outcome (cd-outcome-ds-loan, -partner-only,
   * etc.) lives inside the main content section that sits BEFORE the footer.
   * showComponent() shows the host where it is, so an orphaned host renders
   * below the footer — invisible in practice. Verified on production
   * 2026-05-29: addlInfoParent="body", dsLoanParent="section.section form",
   * "additional-info comes AFTER footer".
   *
   * Anchor on the FIRST other CD outcome in document order — that one is in the
   * real content container, before the footer. Append the host there so it
   * paints in the correct place. Idempotent: no-op once it's in the right
   * parent. Pure DOM move — preserves the node, its children, and any listeners.
   */
  private relocateHostIntoOutcomeContainer(host: HTMLElement): void {
    const outcomes = Array.from(
      document.querySelectorAll<HTMLElement>(
        '[data-component^="cd-outcome-"], [data-component="cd-form-processing"]'
      )
    );
    const anchor = outcomes.find(
      (el) => el !== host && !el.contains(host) && !host.contains(el)
    );
    const desc = (el: Element | null) =>
      el ? `${el.tagName}.${(el.className || '').toString().slice(0, 40)}` : 'null';
    this.dlog('relocate: found', outcomes.length, 'outcome elements. anchor=', anchor ? anchor.getAttribute('data-component') : 'NONE');
    if (!anchor || !anchor.parentElement) {
      this.dlog('relocate: NO anchor with a parent — cannot relocate. Host stays at', desc(host.parentElement));
      return;
    }
    if (host.parentElement === anchor.parentElement) {
      this.dlog('relocate: host ALREADY in correct parent', desc(host.parentElement), '— no move needed.');
      return;
    }
    this.dlog('relocate: MOVING host from', desc(host.parentElement), '→', desc(anchor.parentElement));
    anchor.parentElement.appendChild(host);
  }

  /**
   * Force every multi-step step block visible. The host is a Webflow
   * multi-step form (data-form="multistep" wrapping data-form="step"
   * children) but we treat it as a single-submit form. Two things hide the
   * steps and must be overridden:
   *   1. Webflow's multi-step IX shows only the active step — and it may
   *      never have initialized at all, because the host is display:none on
   *      page load (hideAllOutcomeComponents). Either way, non-active steps
   *      stay hidden.
   *   2. The host wrapper is grid="container" with no intrinsic size, so an
   *      empty (all-steps-hidden) host collapses to 0×0 — blank middle of the
   *      page, and html2canvas captures a zero-byte data:, screenshot.
   *
   * Idempotent and cheap. Called once at bind AND re-asserted by
   * watchVisibility() every time the host's style/class mutates — because the
   * one-time reveal at init can be silently undone later by Webflow IX2 or a
   * re-render that re-hides the steps after the page has settled (observed in
   * production: an O5 rendered blank ~2h after a clean deploy).
   */
  private revealSteps(host: HTMLElement): void {
    // Durable, race-proof reveal: a STYLESHEET rule, not inline styles.
    //
    // Why not inline: a published Webflow site always loads webflow.js, which
    // processes every [data-form="multistep"] form on the page — including this
    // one — and sets step.style.display = 'none' on all but the active step.
    // element.style.display = 'none' OVERWRITES our inline value (dropping the
    // !important with it), and our host-only MutationObserver never sees the
    // step children change. If Webflow runs after our inline reveal, the fields
    // inside steps (Driver's License, State, Time at Address, Job Title, Bank
    // Name, Estimated Credit Score) vanish — only step 1's intro text + the
    // loose non-step fields remain. That's the intermittent blank/partial O5.
    //
    // A stylesheet !important (important author origin) beats an element's
    // inline non-important style in the cascade. Webflow only ever writes inline
    // display:none — it cannot touch our injected <style>. So this wins no
    // matter when Webflow runs. Scoped under the host's data-component so the
    // MAIN lead form's multistep is untouched.
    this.ensureStepRevealStylesheet();

    // Belt-and-suspenders: also set inline for the instant before the stylesheet
    // is consulted. Harmless if Webflow later overwrites it — the rule above is
    // the actual guarantee.
    host.querySelectorAll<HTMLElement>('[data-form="step"]').forEach((step) => {
      step.style.setProperty('display', 'block', 'important');
    });
  }

  private ensureStepRevealStylesheet(): void {
    const STYLE_ID = 'cd-additional-info-step-reveal';
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    // Force the ENTIRE form subtree visible, not just the steps. Webflow's
    // multi-step form JS can clip/mask a PARENT (the .w-form wrapper or the
    // <form> itself — fixed height + overflow:hidden to show one step at a
    // time), which collapses the whole thing regardless of the steps' own
    // display. A step-only rule can't beat a parent clip. Stylesheet
    // !important beats Webflow's inline (non-important) styles, and Webflow
    // can't touch our injected <style>. Scoped to the host so nothing else
    // is affected.
    const H = '[data-component="cd-outcome-additional-information"]';
    style.textContent = [
      // Whole form subtree + the actions container (.processing-block holds the
      // submit button). Webflow multistep hides the actions block until the
      // "last step"; we drive single-submit, so force it visible.
      `${H} .w-form,`,
      `${H} form,`,
      `${H} [data-form="multistep"],`,
      `${H} [data-form="step"],`,
      `${H} .processing-block {`,
      '  display: block !important;',
      '  height: auto !important;',
      '  max-height: none !important;',
      '  min-height: 0 !important;',
      '  overflow: visible !important;',
      '  transform: none !important;',
      '  opacity: 1 !important;',
      '  visibility: visible !important;',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  /**
   * Enable + show the real Submit button. Webflow's multi-step JS disables the
   * submit/next/back buttons (it only enables Submit on the final step) — and
   * once we neutralize multistep, nothing re-enables it. Since this is a
   * single-submit form, force Submit enabled and visible. Idempotent; called at
   * bind and re-asserted on every show via watchVisibility().
   */
  private enableSubmitButton(host: HTMLElement): void {
    host
      .querySelectorAll<HTMLInputElement | HTMLButtonElement>(
        '[data-form="submit-btn"], input[type="submit"]:not([data-form="next-btn"]):not([data-form="back-btn"]), button[type="submit"]'
      )
      .forEach((el) => {
        // Don't re-enable the multistep nav buttons — those stay hidden.
        if (el.getAttribute('data-form') === 'next-btn' || el.getAttribute('data-form') === 'back-btn') return;
        el.disabled = false;
        el.removeAttribute('disabled');
        el.style.removeProperty('display');
        el.style.setProperty('pointer-events', 'auto', 'important');
      });
  }

  /**
   * Strip the data-form="multistep" attribute from the host's form so Webflow's
   * multi-step form JS never treats it as a slider/mask. We drive it as a
   * single-submit form; Webflow's stepping only fights us. Best-effort: if
   * Webflow already initialized it before our bundle ran, the stylesheet above
   * still un-clips the result.
   */
  private neutralizeWebflowMultistep(host: HTMLElement): void {
    host.querySelectorAll<HTMLElement>('[data-form="multistep"]').forEach((el) => {
      el.removeAttribute('data-form');
    });
  }

  /**
   * Hide multi-step back/next nav whenever this outcome view is visible. The
   * additional-info form is a single submit; multi-step navigation doesn't
   * apply. `submit-btn` is intentionally left alone — the additional-info
   * form uses it as its real submit. Restored if the host hides again.
   *
   * Buttons inside AND outside the host are both hidden — Webflow designs
   * may place the multi-step nav either way.
   *
   * Also re-asserts step visibility (revealSteps) whenever the host is shown,
   * so a late IX2 init or re-render that re-hides the steps can't leave the
   * O5/O7 view blank.
   */
  private watchVisibility(host: HTMLElement): void {
    const navSelectors = [
      '[data-form="next-btn"]',
      '[data-form="back-btn"]',
    ];

    const isVisible = (el: HTMLElement): boolean => {
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    };

    const previousInlineDisplay = new WeakMap<HTMLElement, string>();
    let wasVisible = false;

    const apply = () => {
      const hostVisible = isVisible(host);
      // Log only on the transition into visible — that's the O5/O7 "show" moment.
      if (hostVisible && !wasVisible) {
        const r = host.getBoundingClientRect();
        const cs = window.getComputedStyle(host);
        const visibleInputs = Array.from(
          host.querySelectorAll<HTMLElement>('input, select, textarea')
        ).filter((i) => i.getBoundingClientRect().height > 0).length;
        const parent = host.parentElement;
        const hh = (sel: string) => {
          const el = host.querySelector<HTMLElement>(sel);
          if (!el) return 'absent';
          const er = el.getBoundingClientRect();
          const ecs = window.getComputedStyle(el);
          return `h=${Math.round(er.height)} disp=${ecs.display} ovf=${ecs.overflow}`;
        };
        this.dlog('HOST NOW VISIBLE (O5/O7 fired). Diagnostics:', {
          host: `h=${Math.round(r.height)} w=${Math.round(r.width)} disp=${cs.display} ovf=${cs.overflow}`,
          wForm: hh('.w-form'),
          form: hh('form'),
          firstStep: hh('[data-form="step"]'),
          topInPage: Math.round(r.top + window.scrollY),
          visibleInputs,
          parent: parent ? `${parent.tagName}.${(parent.className || '').toString().slice(0, 40)}` : 'null',
        });
        if (r.height < 50) {
          this.dlog('⚠️ HOST height near-zero — shown but collapsed. Check wForm/form heights above: a parent is being clipped.');
        }
      }
      wasVisible = hostVisible;
      // Re-assert step visibility + submit-button enabled every time the host
      // is shown — Webflow's multistep init can re-hide steps / re-disable the
      // submit button after our one-time setup at bind.
      if (hostVisible) {
        this.revealSteps(host);
        this.enableSubmitButton(host);
      }
      navSelectors.forEach((sel) => {
        document.querySelectorAll<HTMLElement>(sel).forEach((btn) => {
          if (hostVisible) {
            if (!previousInlineDisplay.has(btn)) {
              previousInlineDisplay.set(btn, btn.style.display);
            }
            btn.style.display = 'none';
          } else if (previousInlineDisplay.has(btn)) {
            btn.style.display = previousInlineDisplay.get(btn) ?? '';
            previousInlineDisplay.delete(btn);
          }
        });
      });
    };

    apply();

    const observer = new MutationObserver(apply);
    observer.observe(host, { attributes: true, attributeFilter: ['style', 'class'] });
  }

  private findInput(
    host: HTMLElement,
    webflowName: string
  ): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null {
    const selectors = [
      `[data-name="${webflowName}"]`,
      `[name="${webflowName}"]`,
      `[data-name="${webflowName.toLowerCase()}"]`,
    ];
    for (const sel of selectors) {
      const el = host.querySelector(sel) as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | null;
      if (el) return el;
    }
    return null;
  }

  private bindInput(
    input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    rule: AdditionalInfoFieldRule
  ): void {
    const isChoice =
      input.tagName.toLowerCase() === 'select' ||
      (input as HTMLInputElement).type === 'radio' ||
      (input as HTMLInputElement).type === 'checkbox';

    const eventName = isChoice ? 'change' : 'blur';
    input.addEventListener(eventName, () => this.handleFieldEvent(input, rule));

    // Progressive MM/DD/YYYY masking — keystroke-by-keystroke for date fields.
    // Mirrors the main-form DOB UX since the host is opted out of the global
    // format-engine.
    if (!isChoice && ruleNeedsDateMask(rule)) {
      const el = input as HTMLInputElement;
      el.setAttribute('inputmode', 'numeric');
      el.setAttribute('autocomplete', 'off');
      if (!el.getAttribute('maxlength')) el.setAttribute('maxlength', '10');

      el.addEventListener('input', () => {
        const cursorAtEnd = el.selectionStart === el.value.length;
        const masked = applyDateMask(el.value);
        if (masked !== el.value) {
          el.value = masked;
          if (cursorAtEnd) {
            const end = masked.length;
            try { el.setSelectionRange(end, end); } catch { /* non-text inputs ignore */ }
          }
        }
      });

      // Attach a native date picker so users can select instead of typing.
      // Webflow has no date field type, so the field is plain text; we overlay
      // a hidden <input type="date"> and open its picker on focus/click.
      this.attachDatePicker(el, rule);
    }

    // Progressive (XXX) XXX-XXXX masking for phone fields (Work Phone). Mirrors
    // the main-form phone UX; needed because the host is opted out of the
    // global format-engine.
    if (!isChoice && ruleNeedsPhoneMask(rule)) {
      const el = input as HTMLInputElement;
      el.setAttribute('inputmode', 'tel');
      el.setAttribute('autocomplete', 'tel');
      if (!el.getAttribute('maxlength')) el.setAttribute('maxlength', '14'); // (XXX) XXX-XXXX

      el.addEventListener('input', () => {
        const cursorAtEnd = el.selectionStart === el.value.length;
        const masked = applyPhoneMask(el.value);
        if (masked !== el.value) {
          el.value = masked;
          if (cursorAtEnd) {
            const end = masked.length;
            try { el.setSelectionRange(end, end); } catch { /* ignore */ }
          }
        }
      });
    }
  }

  /**
   * Overlay a native date picker on a MM/DD/YYYY text field. Creates a hidden
   * <input type="date"> bound to the same future-date window the validator
   * enforces (today … today+60). Opening the picker writes the selection back
   * into the visible text field as MM/DD/YYYY and runs validation.
   */
  private attachDatePicker(el: HTMLInputElement, rule: AdditionalInfoFieldRule): void {
    const maxDaysAhead =
      (rule.rules.find((r) => r.type === 'future_date')?.params as { maxDaysAhead?: number } | undefined)
        ?.maxDaysAhead ?? 60;

    const picker = document.createElement('input');
    picker.type = 'date';
    // Visually hidden but focusable/openable; sits adjacent to the field.
    picker.style.cssText =
      'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;border:0;padding:0;margin:0;';
    picker.tabIndex = -1;
    picker.setAttribute('aria-hidden', 'true');

    const pad = (n: number) => String(n).padStart(2, '0');
    const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const today = new Date();
    const max = new Date();
    max.setDate(max.getDate() + maxDaysAhead);
    picker.min = toIso(today);
    picker.max = toIso(max);

    // Insert the hidden picker right after the text field.
    el.insertAdjacentElement('afterend', picker);

    // Give the user an affordance that this opens a calendar.
    el.style.cursor = 'pointer';

    const openPicker = () => {
      // Seed the picker with the currently-typed value if valid (MM/DD/YYYY).
      const m = el.value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (m) picker.value = `${m[3]}-${m[1]}-${m[2]}`;
      try {
        // showPicker() requires a user gesture; this runs inside the click/focus handler.
        (picker as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
      } catch {
        /* showPicker unsupported (older browsers) — typing still works */
      }
    };

    el.addEventListener('click', openPicker);
    el.addEventListener('focus', openPicker);

    picker.addEventListener('change', () => {
      if (!picker.value) return;
      const [y, mo, d] = picker.value.split('-');
      el.value = `${mo}/${d}/${y}`;
      this.handleFieldEvent(el, rule); // run validation + partial save
    });
  }

  private handleFieldEvent(
    input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    rule: AdditionalInfoFieldRule
  ): void {
    const value = this.readValue(input);
    this.state.values[rule.payloadKey] = value;
    this.state.touched[rule.payloadKey] = true;

    const errors = this.validateField(value, rule);
    this.state.errors[rule.payloadKey] = errors;
    this.applyFieldErrors(input, errors, rule.payloadKey);

    // Partial save snapshot — fire and forget. Only sends fields the user has touched.
    void sendPartialSave(this.config, this.collectTouchedValues());
  }

  private validateField(value: string, rule: AdditionalInfoFieldRule): string[] {
    const registry = getValidationRegistry();
    const errors: string[] = [];
    for (const r of rule.rules) {
      const res = registry.processRule(r.type, value, r.params);
      errors.push(...res.errors);
    }
    return errors;
  }

  private readValue(
    input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  ): string {
    if ((input as HTMLInputElement).type === 'radio') {
      // For radio, query all siblings with the same name and find the checked one
      const name = (input as HTMLInputElement).name;
      const checked = this.host?.querySelector<HTMLInputElement>(
        `input[type="radio"][name="${name}"]:checked`
      );
      return checked ? checked.value : '';
    }
    if ((input as HTMLInputElement).type === 'checkbox') {
      return (input as HTMLInputElement).checked ? 'true' : 'false';
    }
    return input.value || '';
  }

  /** CSS class for the injected per-field error message element. */
  private static readonly FIELD_ERROR_MSG_CLASS = 'addl-info-field-error';

  /** One message element per field, keyed by payloadKey (NOT by wrapper —
   * several fields can share a `.w-form-row`, so a wrapper-keyed element gets
   * fought over and most fields show nothing). */
  private fieldErrorEls = new Map<string, HTMLElement>();

  /**
   * Show/clear validation feedback on a single field. Renders the first
   * validator message right under that field (so the user knows WHAT'S wrong,
   * not just that something is), flags the input with a red border (visible
   * without depending on the Webflow design having `.field-error` styling),
   * and clears both when the field becomes valid.
   */
  private applyFieldErrors(
    input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    errors: string[],
    payloadKey: string
  ): void {
    const hasError = errors.length > 0;
    input.setAttribute('aria-invalid', hasError ? 'true' : 'false');

    const isChoice =
      (input as HTMLInputElement).type === 'radio' ||
      (input as HTMLInputElement).type === 'checkbox';

    // Red border on the field itself (skip radios/checkboxes — looks wrong).
    if (!isChoice) input.style.borderColor = hasError ? '#b42318' : '';

    const wrapper =
      (input.closest('.w-form-row') as HTMLElement | null) || input.parentElement;
    if (wrapper) {
      if (hasError) wrapper.classList.add(FIELD_ERROR_CLASS);
      else wrapper.classList.remove(FIELD_ERROR_CLASS);
    }

    let msgEl = this.fieldErrorEls.get(payloadKey);
    if (hasError) {
      if (!msgEl) {
        msgEl = document.createElement('div');
        msgEl.className = AdditionalInfoFormController.FIELD_ERROR_MSG_CLASS;
        msgEl.setAttribute('aria-live', 'polite');
        msgEl.style.cssText =
          'color:#b42318;font-size:13px;line-height:1.3;margin-top:4px;';
        // Place it right after the field. For radio groups, drop it at the end
        // of the group's wrapper so it sits below all the options.
        if (isChoice && wrapper) wrapper.appendChild(msgEl);
        else if (input.parentElement) input.insertAdjacentElement('afterend', msgEl);
        else if (wrapper) wrapper.appendChild(msgEl);
        this.fieldErrorEls.set(payloadKey, msgEl);
      }
      // First message is the most relevant (required > format/length).
      msgEl.textContent = errors[0];
      msgEl.style.display = 'block';
    } else if (msgEl) {
      msgEl.style.display = 'none';
      msgEl.textContent = '';
    }
  }

  private bindSubmit(host: HTMLElement): void {
    // Prefer the explicit Webflow submit button; fall back to any input[type=submit] or button[type=submit].
    const candidates = host.querySelectorAll<HTMLElement>(
      '[data-form="submit-btn"], input[type="submit"], button[type="submit"]'
    );
    this.dlog('bindSubmit: found', candidates.length, 'submit candidate(s):',
      Array.from(candidates).map((b) => b.getAttribute('data-form') || (b as HTMLInputElement).type));
    candidates.forEach((btn) => {
      if (this.boundButtons.has(btn)) return;
      this.boundButtons.add(btn);
      // Capture phase so we beat Webflow's own form handler / any other
      // bubble-phase listener that might stop propagation before us.
      btn.addEventListener('click', (e) => this.handleSubmit(e), true);
    });

    // Also trap form submit so the Webflow form's default POST doesn't fire.
    const form = host.querySelector('form');
    if (form && !this.boundButtons.has(form)) {
      this.boundButtons.add(form);
      form.addEventListener('submit', (e) => this.handleSubmit(e), true);
      this.dlog('bindSubmit: bound form submit listener (capture).');
    } else {
      this.dlog('bindSubmit: no <form> found in host to bind submit on.');
    }
  }

  /**
   * Disable / re-enable the form's submit button(s) so a user can't double-
   * submit while the dcapi call is in flight. Uses Webflow's `data-wait`
   * attribute for the "Please wait..." label when present, and restores the
   * original label on re-enable.
   */
  private setSubmitButtonsDisabled(disabled: boolean): void {
    const host = this.host;
    if (!host) return;
    const btns = host.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
      '[data-form="submit-btn"], input[type="submit"], button[type="submit"]'
    );
    btns.forEach((el) => {
      el.disabled = disabled;
      if (disabled) {
        el.classList.add('is-submitting');
        el.setAttribute('aria-busy', 'true');
        const wait = el.getAttribute('data-wait');
        if (wait) {
          if (el instanceof HTMLInputElement) {
            if (el.dataset.originalValue === undefined) el.dataset.originalValue = el.value;
            el.value = wait;
          } else {
            if (el.dataset.originalValue === undefined) el.dataset.originalValue = el.textContent ?? '';
            el.textContent = wait;
          }
        }
      } else {
        el.classList.remove('is-submitting');
        el.removeAttribute('aria-busy');
        if (el.dataset.originalValue !== undefined) {
          if (el instanceof HTMLInputElement) el.value = el.dataset.originalValue;
          else el.textContent = el.dataset.originalValue;
          delete el.dataset.originalValue;
        }
      }
    });
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    e.stopPropagation();
    this.dlog('SUBMIT clicked. event type:', e.type, 'target:', (e.target as HTMLElement)?.getAttribute?.('data-form') || (e.target as HTMLElement)?.tagName);

    if (this.submitting) {
      this.dlog('SUBMIT ignored — already submitting (double-click guard).');
      return;
    }

    // Validate every known field, treating missing-from-DOM as a no-op (don't fail).
    let firstInvalid: HTMLElement | null = null;
    const invalidKeys: string[] = [];
    const missingKeys: string[] = [];
    for (const rule of ADDITIONAL_INFO_FIELDS) {
      const input = this.inputs.get(rule.payloadKey);
      if (!input) { missingKeys.push(rule.payloadKey); continue; }
      const value = this.readValue(input);
      this.state.values[rule.payloadKey] = value;
      this.state.touched[rule.payloadKey] = true;
      const errors = this.validateField(value, rule);
      this.state.errors[rule.payloadKey] = errors;
      this.applyFieldErrors(input, errors, rule.payloadKey);
      if (errors.length > 0) {
        invalidKeys.push(`${rule.payloadKey}(${errors.join('; ')})`);
        if (!firstInvalid) firstInvalid = input;
      }
    }
    this.dlog('SUBMIT validation:', {
      invalidCount: invalidKeys.length,
      invalid: invalidKeys,
      notBoundInDom: missingKeys,
    });

    if (firstInvalid) {
      const count = invalidKeys.length;
      this.showFormMessage(
        count === 1
          ? 'Please correct the highlighted field before submitting.'
          : `Please correct the ${count} highlighted fields before submitting.`,
        'error'
      );
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalid.focus({ preventScroll: true });
      this.dlog('SUBMIT BLOCKED by validation — fix the invalid fields above. No POST sent.');
      return;
    }

    this.clearFormMessage();

    this.submitting = true;
    this.setSubmitButtonsDisabled(true);

    this.dlog('SUBMIT POSTing to dcapi:', this.config.submitUrl);
    const outcome = await submitAdditionalInfo(this.config, this.collectAllValues());
    this.dlog('SUBMIT dcapi outcome:', {
      ok: outcome.ok,
      accepted: outcome.accepted,
      scenario: outcome.scenario,
      redirectUrl: outcome.redirectUrl,
      error: outcome.error,
    });

    if (!outcome.ok) {
      // Failed — let the user retry. Re-enable and clear the guard.
      this.submitting = false;
      this.setSubmitButtonsDisabled(false);
      this.showFormMessage(
        outcome.error
          ? `We couldn't submit your information: ${outcome.error}`
          : "We couldn't submit your information. Please try again.",
        'error'
      );
      if (this.config.debug) console.warn('[additional-info] submit error:', outcome.error);
      return;
    }

    if (outcome.accepted) {
      if (outcome.redirectUrl) {
        // Accepted — keep the button disabled through the redirect so a second
        // click can't fire during navigation.
        performAcceptRedirect(outcome.redirectUrl, this.config.debug);
        return;
      }
      // Accepted but no redirect URL — don't silently fall through to the
      // non-accept path (which would look like nothing happened). Surface it.
      this.submitting = false;
      this.setSubmitButtonsDisabled(false);
      this.showFormMessage(
        "Your application was approved but we didn't receive a redirect link. Please try again or contact support.",
        'error'
      );
      if (this.config.debug) {
        console.warn('[additional-info] accepted but no redirect URL in response:', outcome.raw);
      }
      return;
    }

    // Non-accept but ok: the component-renderer will swap the view to another
    // outcome. Clear the guard but leave the button disabled until the view
    // changes (re-enable so an in-place retry is possible).
    this.submitting = false;
    this.setSubmitButtonsDisabled(false);

    // Non-accept path: dispatch a DOM event so the existing component-renderer
    // can route to whatever scenario the response carried (handled outside
    // this module — wired in the lead-tracker init).
    window.dispatchEvent(
      new CustomEvent('additional-info:non-accept-response', {
        detail: { outcome },
      })
    );
  }

  private collectTouchedValues(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, val] of Object.entries(this.state.values)) {
      if (this.state.touched[key]) out[key] = val;
    }
    return out;
  }

  private collectAllValues(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const rule of ADDITIONAL_INFO_FIELDS) {
      out[rule.payloadKey] = this.state.values[rule.payloadKey] ?? '';
    }
    return out;
  }

  /** ID for the injected message banner so we reuse one node. */
  private static readonly MESSAGE_ID = 'addl-info-form-message';

  /**
   * Render (or update) a visible banner inside the form so a blocked submit
   * or a dcapi error is never silent. Without this the user clicks Submit,
   * validation quietly blocks, and it looks like "nothing happened".
   */
  private showFormMessage(message: string, kind: 'error' | 'info'): void {
    if (!this.host) return;
    const form = this.host.querySelector('form') || this.host;
    let banner = this.host.querySelector<HTMLDivElement>(
      `#${AdditionalInfoFormController.MESSAGE_ID}`
    );
    if (!banner) {
      banner = document.createElement('div');
      banner.id = AdditionalInfoFormController.MESSAGE_ID;
      banner.setAttribute('role', 'alert');
      banner.setAttribute('aria-live', 'assertive');
      // Insert before the submit button if we can find it, else at the end.
      const submitBtn = form.querySelector('[data-form="submit-btn"]');
      if (submitBtn && submitBtn.parentElement) {
        submitBtn.parentElement.insertBefore(banner, submitBtn);
      } else {
        form.appendChild(banner);
      }
    }
    const isError = kind === 'error';
    banner.style.cssText = [
      'display:block',
      'margin:12px 0',
      'padding:12px 16px',
      'border-radius:8px',
      'font-size:14px',
      'line-height:1.4',
      `color:${isError ? '#b42318' : '#1849a9'}`,
      `background:${isError ? '#fef3f2' : '#eff8ff'}`,
      `border:1px solid ${isError ? '#fda29b' : '#b2ddff'}`,
    ].join(';');
    banner.textContent = message;
  }

  private clearFormMessage(): void {
    if (!this.host) return;
    const banner = this.host.querySelector<HTMLElement>(
      `#${AdditionalInfoFormController.MESSAGE_ID}`
    );
    if (banner) banner.style.display = 'none';
  }
}
